import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/LoadingSpinner';
import orderTrackingService from '../../services/orderTrackingService';

/**
 * OrderHistory Component
 * 
 * Displays user's order history with expandable order details and real-time tracking
 * Fetches orders with order_items, products, and shipment tracking using Supabase join query
 */
const OrderHistory = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [trackingData, setTrackingData] = useState({});

  useEffect(() => {
    loadOrders();
  }, [user]);

  const loadOrders = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError('');

      // Fetch orders with order_items and products using Supabase join
      const { data, error: fetchError } = await supabase?.from('orders')?.select(`
          *,
          order_items (
            *,
            products (*)
          )
        `)?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setOrders(data || []);
      
      // Load tracking data for each order
      const tracking = {};
      for (const order of data || []) {
        try {
          const shipments = await orderTrackingService?.getOrderShipments(order?.id);
          if (shipments?.length > 0) {
            tracking[order?.id] = shipments;
          }
        } catch (err) {
          console.error(`Error loading tracking for order ${order?.id}:`, err);
        }
      }
      setTrackingData(tracking);
    } catch (err) {
      console.error('Error loading orders:', err);
      setError(err?.message || 'Siparişler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(orderId)) {
        newSet?.delete(orderId);
      } else {
        newSet?.add(orderId);
      }
      return newSet;
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Beklemede', className: 'bg-yellow-100 text-yellow-800' },
      processing: { label: 'İşleniyor', className: 'bg-blue-100 text-blue-800' },
      shipped: { label: 'Kargoya Verildi', className: 'bg-purple-100 text-purple-800' },
      delivered: { label: 'Teslim Edildi', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'İptal Edildi', className: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config?.className}`}>
        {config?.label}
      </span>
    );
  };

  const getShipmentStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Hazırlanıyor', className: 'bg-gray-100 text-gray-800', icon: 'Package' },
      label_created: { label: 'Etiket Oluşturuldu', className: 'bg-blue-100 text-blue-800', icon: 'FileText' },
      picked_up: { label: 'Kargo Alındı', className: 'bg-indigo-100 text-indigo-800', icon: 'Truck' },
      in_transit: { label: 'Yolda', className: 'bg-purple-100 text-purple-800', icon: 'Navigation' },
      out_for_delivery: { label: 'Dağıtımda', className: 'bg-orange-100 text-orange-800', icon: 'MapPin' },
      delivered: { label: 'Teslim Edildi', className: 'bg-green-100 text-green-800', icon: 'CheckCircle' },
      failed: { label: 'Başarısız', className: 'bg-red-100 text-red-800', icon: 'XCircle' },
      returned: { label: 'İade Edildi', className: 'bg-yellow-100 text-yellow-800', icon: 'RotateCcw' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config?.className}`}>
        <Icon name={config?.icon} size={14} />
        {config?.label}
      </span>
    );
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    })?.format(price);
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main-content bg-background min-h-screen">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
            <div className="bg-card rounded-xl shadow-organic p-8">
              <LoadingSpinner />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main-content bg-background min-h-screen">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
            <div className="bg-card rounded-xl shadow-organic p-8">
              <div className="text-center text-red-600">
                <Icon name="AlertCircle" size={48} className="mx-auto mb-4" />
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (orders?.length === 0) {
    return (
      <>
        <Header />
        <div className="main-content bg-background min-h-screen">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
            <div className="bg-card rounded-xl shadow-organic p-8 text-center">
              <Icon name="Package" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">Henüz sipariş geçmişiniz yok</p>
              <Link 
                to="/official-store" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth"
              >
                <Icon name="ShoppingBag" size={20} />
                Alışverişe Başla
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main-content bg-background min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Sipariş Geçmişi</h1>
            <p className="text-muted-foreground">Tüm siparişlerinizi ve kargo takip bilgilerinizi görüntüleyin</p>
          </div>

          <div className="space-y-4">
            {orders?.map((order) => {
              const isExpanded = expandedOrders?.has(order?.id);
              const orderShipments = trackingData?.[order?.id] || [];
              const hasTracking = orderShipments?.length > 0;

              return (
                <div key={order?.id} className="bg-card rounded-xl shadow-organic overflow-hidden">
                  {/* Order Header */}
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-foreground">
                            Sipariş #{order?.id?.slice(0, 8)}
                          </h3>
                          {getStatusBadge(order?.status)}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(order?.created_at), 'dd MMMM yyyy HH:mm', { locale: tr })}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground mb-1">Toplam</p>
                          <p className="text-xl font-bold text-foreground">
                            {formatPrice(order?.total_amount)}
                          </p>
                        </div>
                        <button
                          onClick={() => toggleOrderExpansion(order?.id)}
                          className="p-2 hover:bg-muted rounded-lg transition-smooth"
                        >
                          <Icon 
                            name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                            size={24} 
                            className="text-muted-foreground"
                          />
                        </button>
                      </div>
                    </div>

                    {/* Tracking Summary */}
                    {hasTracking && (
                      <div className="bg-muted/50 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Icon name="Truck" size={20} className="text-primary" />
                          <h4 className="text-sm font-bold text-foreground">Kargo Takibi</h4>
                        </div>
                        {orderShipments?.map((shipment) => (
                          <div key={shipment?.id} className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3 last:mb-0">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {getShipmentStatusBadge(shipment?.status)}
                                <span className="text-xs text-muted-foreground">
                                  {shipment?.carrier?.name}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Takip No: <span className="font-mono font-medium text-foreground">{shipment?.trackingNumber}</span>
                              </p>
                              {shipment?.trackingEvents?.length > 0 && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Son Durum: {shipment?.trackingEvents?.[0]?.eventDescription}
                                  {shipment?.trackingEvents?.[0]?.location && ` - ${shipment?.trackingEvents?.[0]?.location}`}
                                </p>
                              )}
                            </div>
                            {shipment?.estimatedDeliveryAt && (
                              <div className="text-right">
                                <p className="text-xs text-muted-foreground mb-1">Tahmini Teslimat</p>
                                <p className="text-sm font-medium text-foreground">
                                  {format(new Date(shipment?.estimatedDeliveryAt), 'dd MMM yyyy', { locale: tr })}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                        <Link
                          to="/order-tracking-dashboard"
                          className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium mt-2"
                        >
                          <Icon name="ExternalLink" size={16} />
                          Detaylı Takip
                        </Link>
                      </div>
                    )}

                    {/* Shipping Address */}
                    {order?.shipping_address && (
                      <div className="bg-muted/50 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name="MapPin" size={16} className="text-muted-foreground" />
                          <h4 className="text-sm font-bold text-foreground">Teslimat Adresi</h4>
                        </div>
                        <p className="text-sm text-foreground">
                          {order?.shipping_address?.full_name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {order?.shipping_address?.address}, {order?.shipping_address?.city}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {order?.shipping_address?.phone}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Expanded Order Details */}
                  {isExpanded && (
                    <div className="border-t border-border p-6 bg-muted/20">
                      <h4 className="text-sm font-bold text-foreground mb-4">Sipariş Detayları</h4>
                      <div className="space-y-3">
                        {order?.order_items?.map((item) => {
                          const product = item?.products;
                          return (
                            <div key={item?.id} className="flex gap-4 pb-3 border-b border-border last:border-0">
                              <div className="h-16 w-16 flex-shrink-0 rounded-lg overflow-hidden bg-muted">
                                <img
                                  src={product?.images?.[0] || '/assets/images/no_image.png'}
                                  alt={`${product?.title} ürün görseli`}
                                  className="h-full w-full object-cover"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">
                                  {product?.title}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Adet: {item?.quantity} × {formatPrice(item?.price_at_purchase)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-bold text-foreground">
                                  {formatPrice(parseFloat(item?.price_at_purchase) * item?.quantity)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Tracking Timeline */}
                      {hasTracking && orderShipments?.[0]?.trackingEvents?.length > 0 && (
                        <div className="mt-6">
                          <h4 className="text-sm font-bold text-foreground mb-4">Kargo Hareketleri</h4>
                          <div className="space-y-3">
                            {orderShipments?.[0]?.trackingEvents?.map((event, index) => (
                              <div key={event?.id} className="flex gap-3">
                                <div className="flex flex-col items-center">
                                  <div className={`w-3 h-3 rounded-full ${
                                    index === 0 ? 'bg-primary' : 'bg-muted-foreground'
                                  }`} />
                                  {index < orderShipments?.[0]?.trackingEvents?.length - 1 && (
                                    <div className="w-0.5 h-full bg-border mt-1" />
                                  )}
                                </div>
                                <div className="flex-1 pb-4">
                                  <p className={`text-sm font-medium ${
                                    index === 0 ? 'text-primary' : 'text-foreground'
                                  }`}>
                                    {event?.eventDescription}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1">
                                    {event?.location && (
                                      <p className="text-xs text-muted-foreground">
                                        {event?.location}
                                      </p>
                                    )}
                                    <span className="text-xs text-muted-foreground">
                                      {format(new Date(event?.eventTimestamp), 'dd MMM HH:mm', { locale: tr })}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderHistory;